<script setup>
import { ref } from 'vue'
import { useAuthStore } from '@/stores/authStore'

const authStore = useAuthStore()
const isLoading = ref(null) // Track which provider is loading

const handleLogin = async (provider) => {
  isLoading.value = provider
  try {
    await authStore.login(provider)
  } finally {
    isLoading.value = null
  }
}
</script>

<template>
  <div class="min-h-screen flex items-center justify-center bg-[#f9fafb] py-12 px-4 sm:px-6 lg:px-8">
    <div
      class="max-w-110 w-full space-y-8 bg-white p-8 sm:p-12 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100">
      <div class="flex flex-col items-center">
        <div class="flex items-center justify-center gap-2 pb-6 border-b border-gray-100">
          <svg class="w-8 h-8" viewBox="0 0 256 256">
            <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
              <g>
                <rect stroke="#000000" stroke-width="16" x="38" y="38" width="180" height="180" rx="18"></rect>
                <rect fill="#000000" x="91" y="96" width="24" height="64" rx="12"></rect>
                <rect fill="#000000" x="139" y="96" width="24" height="64" rx="12"></rect>
                <rect fill="#000000" x="95" y="116" width="64" height="24" rx="12"></rect>
                <rect fill="#000000" x="79" y="0" width="24" height="64" rx="12"></rect>
                <rect fill="#000000" x="152" y="0" width="24" height="64" rx="12"></rect>
                <rect fill="#000000" x="80" y="192" width="24" height="64" rx="12"></rect>
                <rect fill="#000000" x="152" y="192" width="24" height="64" rx="12"></rect>
                <rect fill="#000000" x="0" y="80" width="64" height="24" rx="12"></rect>
                <rect fill="#000000" x="0" y="152" width="64" height="24" rx="12"></rect>
                <rect fill="#000000" x="192" y="80" width="64" height="24" rx="12"></rect>
                <rect fill="#000000" x="192" y="152" width="64" height="24" rx="12"></rect>
              </g>
            </g>
          </svg>
          <span class="text-xl font-extrabold tracking-tight text-gray-900">HasMCP</span>
        </div>
        <h2 class="mt-4 text-2xl font-medium text-gray-900">Sign in to your account</h2>
      </div>

      <div class="mt-10 space-y-3">
        <button @click="handleLogin('github')" :disabled="isLoading"
          class="group w-full flex items-center justify-center py-2.5 px-4 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 active:scale-[0.98] disabled:opacity-50">
          <svg v-if="isLoading !== 'github'" class="h-5 w-5 mr-3 text-gray-900" fill="currentColor" viewBox="0 0 24 24">
            <path
              d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
          </svg>
          <span v-else class="mr-3 h-4 w-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></span>
          Continue with GitHub
        </button>

        <button @click="handleLogin('google')" :disabled="isLoading"
          class="group w-full flex items-center justify-center py-2.5 px-4 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 active:scale-[0.98] disabled:opacity-50">
          <svg v-if="isLoading !== 'google'" class="h-5 w-5 mr-3" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4" />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853" />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05" />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335" />
          </svg>
          <span v-else class="mr-3 h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></span>
          Continue with Google
        </button>

        <button @click="handleLogin('gitlab')" :disabled="isLoading"
          class="group w-full flex items-center justify-center py-2.5 px-4 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 active:scale-[0.98] disabled:opacity-50">
          <svg v-if="isLoading !== 'gitlab'" class="h-5 w-5 mr-3" viewBox="0 0 24 24">
            <path
              d="M22.65 14.39L20.21 6.91a.63.63 0 0 0-.2-.3.61.61 0 0 0-.36-.11.61.61 0 0 0-.36.11.63.63 0 0 0-.2.3l-2.44 7.48H7.35L4.91 6.91a.63.63 0 0 0-.2-.3.61.61 0 0 0-.36-.11.61.61 0 0 0-.36.11.63.63 0 0 0-.2.3L1.35 14.39a.83.83 0 0 0 .3 1.05l10.15 7.4a.4.4 0 0 0 .47 0l10.15-7.4a.83.83 0 0 0 .23-1.05z"
              fill="#E24329" />
          </svg>
          <span v-else
            class="mr-3 h-4 w-4 border-2 border-gray-300 border-t-orange-600 rounded-full animate-spin"></span>
          Continue with GitLab
        </button>
      </div>

      <div class="mt-8 text-center">
        <p class="text-[12px] leading-relaxed text-gray-400 px-4">
          By continuing, you agree to our
          <a href="https://hasmcp.com/tos.html" class="underline hover:text-gray-600 transition-colors">Terms of
            Service</a>
          and
          <a href="https://hasmcp.com/privacy.html" class="underline hover:text-gray-600 transition-colors">Privacy
            Policy</a>.
        </p>
      </div>

      <div class="pt-6 border-t border-gray-100 text-center">
        <router-link to="/signup" class="text-sm font-medium text-gray-600 hover:text-black transition-colors">
          Don't have an account? <span class="text-black underline underline-offset-4">Sign up</span>
        </router-link>
      </div>
    </div>
  </div>
</template>